import { classMap, Component, html, LitElement, property, state, unsafeCSS } from '@rxdi/lit-html';
import * as JsonInputComponentCSS from 'bundle-text:./json-editor.component.css';

type MutableValidity = {
  -readonly [K in keyof ValidityState]: ValidityState[K];
};

/**
 * @customElement json-input
 */
@Component({
  selector: 'json-input',
  styles: [unsafeCSS(JsonInputComponentCSS)],
  template(this: JsonInputComponent) {
    return html`
      <div class="editor-wrapper">
        <textarea
          part="textarea"
          class=${classMap({ outline: !this.readonly })}
          spellcheck="false"
          ?disabled="${this.disabled}"
          ?readonly="${this.readonly}"
          ?required="${this.required}"
          .tabIndex="${this.disabled ? -1 : 0}"
          aria-label="${this.ariaLabel || 'JSON Editor'}"
          aria-invalid="${!this.isValid}"
          aria-required="${this.required}"
          @input="${this.handleInput}"
          @change="${this.handleChange}"
          @blur="${this.handleBlur}"
          @focus="${this.handleFocus}"
          @scroll="${this.syncScroll}"
          @keydown="${this.handleTab}"
        ></textarea>
        <div class="highlight-layer" .innerHTML="${this.highlightSyntax(this.value)}"></div>
        <div class="status-bar">
          <span class="${this.isValid ? '' : 'error'}">
            ${this.isValid ? '✓ Valid JSON' : `✗ ${this.customValidationMessage || 'Invalid JSON'}`}
          </span>
          <span>${this.value?.length} characters</span>
        </div>
      </div>
    `;
  },
})
export class JsonInputComponent extends LitElement {
  // Form-associated custom element
  static formAssociated = true;

  private _internals: ElementInternals;
  private _value = '{\n  "framework": "@rxdi/lit-html",\n  "status": "ready"\n}';
  private _customValidationMessage = '';
  private _isUserInput = false;

  @property({ type: String, reflect: true }) theme: 'black' | 'white' = 'black';
  @property({ type: String, reflect: true }) name = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) autoStringifyValues = false; // New feature!
  @property({ type: String }) ariaLabel = '';
  @property({ type: String }) placeholder = '';
  @property({ type: Number }) minLength = 0;
  @property({ type: Number }) maxLength = Infinity;
  @property({ type: Boolean }) beautify = true;

  @property({ type: String })
  get value(): string {
    return this._value;
  }
  set value(val: string) {
    const oldVal = this._value;

    // Apply expansion if autoStringifyValues is enabled
    if (this.autoStringifyValues && val !== oldVal) {
      this._value = this.expandStringifiedJson(val);
    } else if (this.beautify && val !== oldVal) {
      try {
        const parsed = JSON.parse(val);
        this._value = JSON.stringify(parsed, null, 2);
      } catch (e) {
        this._value = val;
      }
    } else {
      this._value = val;
    }

    this.requestUpdate('value', oldVal);
    this.validateAndUpdateForm();
  }

  @state() private isValid = true;
  @state() private customValidationMessage = '';

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  OnUpdateFirst() {
    // Parse and expand stringified JSON values if autoStringifyValues is enabled
    if (this.autoStringifyValues) {
      this._value = this.expandStringifiedJson(this._value);
    } else {
      // Only convert if it's valid JSON on first load
      try {
        JSON.parse(this._value);
        this._value = this.convertJson(this._value);
      } catch (e) {
        // Keep the value as-is if it's not valid JSON
      }
    }

    this.validateAndUpdateForm();

    // Set initial textarea value
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      textarea.value = this._value;
    }
  }

  updated(changedProperties: Map<string, string>) {
    super.updated(changedProperties);

    // Handle autoStringifyValues changes
    if (changedProperties.has('autoStringifyValues') && this.autoStringifyValues) {
      this._value = this.expandStringifiedJson(this._value);
      const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.value = this._value;
      }
      this.validateAndUpdateForm();
    }

    // Only update textarea value if it was changed programmatically (not from user input)
    if (changedProperties.has('value') && !this._isUserInput) {
      const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea && textarea.value !== this._value) {
        textarea.value = this._value;
      }
    }
    this._isUserInput = false;
  }

  connectedCallback() {
    super.connectedCallback();
    // Set initial ARIA role
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'textbox');
    }
  }

  private validateAndUpdateForm() {
    const validityState: Partial<MutableValidity> = {};
    let validationMessage = '';

    // Check required
    if (this.required && (!this._value || this._value.trim() === '')) {
      validityState.valueMissing = true;
      validationMessage = 'This field is required';
    }
    // Check custom validation message
    else if (this._customValidationMessage) {
      validityState.customError = true;
      validationMessage = this._customValidationMessage;
    }
    // Check JSON validity
    else {
      try {
        JSON.parse(this._value);
        this.isValid = true;

        // Check length constraints
        if (this.minLength && this._value.length < this.minLength) {
          validityState.tooShort = true;
          validationMessage = `Minimum length is ${this.minLength} characters`;
        } else if (this.maxLength && this._value.length > this.maxLength) {
          validityState.tooLong = true;
          validationMessage = `Maximum length is ${this.maxLength} characters`;
        }
      } catch (err) {
        this.isValid = false;
        validityState.customError = true;
        validationMessage = 'Invalid JSON format';
      }
    }

    // Update form internals
    if (Object.keys(validityState).length > 0) {
      this._internals.setValidity(
        validityState,
        validationMessage,
        this.shadowRoot?.querySelector('textarea') || undefined
      );
    } else {
      this._internals.setValidity({});
    }

    // Set form value with proper format
    const formValue =
      this.autoStringifyValues && this.isValid ? this.collapseToStringifiedJson(this._value) : this._value;

    this._internals.setFormValue(formValue);
    this.customValidationMessage = validationMessage;
  }

  private handleInput(e: Event) {
    if (this.readonly) return;

    const target = e.target as HTMLTextAreaElement;
    this._isUserInput = true;
    this._value = target.value;

    this.validateAndUpdateForm();
    this.syncScroll();
    this.requestUpdate(); // Update only the highlight layer and status

    // Dispatch input event
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));

    if (this.isValid) {
      this.sendChangeEvent();
    }
    this.dispatchValidityEvent();
  }

  private handleChange(e: Event) {
    // Dispatch change event
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private handleBlur(e: FocusEvent) {
    // Beautify the JSON here
    if (this.isValid && this.beautify) {
      try {
        const parsed = JSON.parse(this._value);
        this._value = JSON.stringify(parsed, null, 2);
        const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
        if (textarea) {
          textarea.value = this._value;
        }
        this.validateAndUpdateForm();
        this.requestUpdate();
      } catch (e) {
        // Invalid JSON, skip formatting
      }
    }

    this.dispatchEvent(new FocusEvent('blur', { bubbles: true, composed: true }));
  }

  private handleFocus(e: FocusEvent) {
    this.dispatchEvent(new FocusEvent('focus', { bubbles: true, composed: true }));
  }

  private sendChangeEvent() {
    // Collapse nested objects back to stringified values if autoStringifyValues is enabled
    const finalValue = this.autoStringifyValues
      ? this.collapseToStringifiedJson(this._value)
      : this.convertJson(this._value);

    this.dispatchEvent(
      new CustomEvent('onValueChange', {
        detail: finalValue,
        bubbles: true,
        composed: true,
      })
    );
  }

  private dispatchValidityEvent() {
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: this.isValid,
        bubbles: true,
        composed: true,
      })
    );
  }

  private syncScroll() {
    const textarea = this.shadowRoot?.querySelector('textarea');
    const highlight = this.shadowRoot?.querySelector('.highlight-layer');
    if (textarea && highlight) {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    }
  }

  private handleTab(e: KeyboardEvent) {
    if (this.readonly) return;

    if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();

      // Use execCommand to preserve undo history
      document.execCommand('insertText', false, '  ');
    }
  }

  private highlightSyntax(json: string) {
    if (!json) return '';
    const escaped = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'number';
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? 'key' : 'string';
        } else if (/true|false/.test(match)) {
          cls = 'boolean';
        } else if (/null/.test(match)) {
          cls = 'null';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  }

  private convertJson(json: string) {
    const data = this.safeParse(json);

    return JSON.stringify(
      Object.keys(data).reduce((prev, curr) => ({ ...prev, [curr]: this.safeParse(data[curr]) }), {}),
      null,
      2
    );
  }

  private safeParse(json: string) {
    try {
      return JSON.parse(json);
    } catch (e) {
      return json;
    }
  }

  /**
   * Expands stringified JSON values into actual objects for editing
   * Example: {"key": "{\"nested\":\"value\"}"} -> {"key": {"nested": "value"}}
   */
  private expandStringifiedJson(jsonString: string): string {
    try {
      const obj = JSON.parse(jsonString);
      const expanded = this.expandObject(obj);
      return JSON.stringify(expanded, null, 2);
    } catch (e) {
      return jsonString;
    }
  }

  private expandObject(obj: Record<string, string>) {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.expandObject(item));
    }

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        // Try to parse the string value as JSON
        try {
          const parsed = JSON.parse(value);
          // If it's an object or array, expand it recursively
          if (typeof parsed === 'object' && parsed !== null) {
            result[key] = this.expandObject(parsed);
          } else {
            result[key] = value;
          }
        } catch (e) {
          // Not valid JSON, keep as string
          result[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.expandObject(value);
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  /**
   * Collapses nested objects back to stringified values
   * Example: {"key": {"nested": "value"}} -> {"key": "{\"nested\":\"value\"}"}
   */
  private collapseToStringifiedJson(jsonString: string): string {
    try {
      const obj = JSON.parse(jsonString);
      const collapsed = this.collapseObject(obj);
      return JSON.stringify(collapsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  }

  private collapseObject(obj: Record<string, string>, depth = 0) {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.collapseObject(item, depth + 1));
    }

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null && depth === 0) {
        // At the top level, stringify nested objects
        result[key] = JSON.stringify(value);
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.collapseObject(value, depth + 1);
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  // ============================================
  // FORM ELEMENT API - Complete Implementation
  // ============================================

  // Form-associated custom element callbacks
  formResetCallback() {
    this._value = '{\n  "framework": "@rxdi/lit-html",\n  "status": "ready"\n}';
    this._customValidationMessage = '';
    this.isValid = true;
    this.validateAndUpdateForm();
    this.requestUpdate();
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state: string, mode: 'restore' | 'autocomplete') {
    this._value = state;
    this.validateAndUpdateForm();
  }

  // Standard form control properties
  get form() {
    return this._internals.form;
  }

  get type() {
    return 'json-editor';
  }

  get validity() {
    return this._internals.validity;
  }

  get validationMessage() {
    return this._internals.validationMessage;
  }

  get willValidate() {
    return this._internals.willValidate;
  }

  // Validation methods
  checkValidity(): boolean {
    return this._internals.checkValidity();
  }

  reportValidity(): boolean {
    return this._internals.reportValidity();
  }

  setCustomValidity(message: string): void {
    this._customValidationMessage = message;
    this.validateAndUpdateForm();
  }

  // Focus management
  focus(options?: FocusOptions): void {
    const textarea = this.shadowRoot?.querySelector('textarea');
    textarea?.focus(options);
  }

  blur(): void {
    const textarea = this.shadowRoot?.querySelector('textarea');
    textarea?.blur();
  }

  // Selection API
  select(): void {
    const textarea = this.shadowRoot?.querySelector('textarea');
    textarea?.select();
  }

  setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none'): void {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    textarea?.setSelectionRange(start, end, direction);
  }

  setRangeText(replacement: string, start?: number, end?: number, selectMode?: SelectionMode): void {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      if (start !== undefined && end !== undefined) {
        textarea.setRangeText(replacement, start, end, selectMode);
      } else {
        textarea.setRangeText(replacement);
      }
      this._value = textarea.value;
      this.validateAndUpdateForm();
    }
  }

  // Getters for selection
  get selectionStart(): number | null {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    return textarea?.selectionStart ?? null;
  }

  get selectionEnd(): number | null {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    return textarea?.selectionEnd ?? null;
  }

  get selectionDirection(): string | null {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    return textarea?.selectionDirection ?? null;
  }

  // Labels association (returns associated label elements)
  get labels() {
    return this._internals.labels;
  }
}
